import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { IconButton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { LayoutGrid, LayoutGridItem } from 'app/layout/LayoutGrid';
import React, { useEffect, useRef, useState } from 'react';
import { customSwitch } from 'utils/customSwitchCase/customSwitch';
import { flattenedNotificationTileData } from '../../Notification/KPIMetric/Helper';
import { TabTitle } from '../../PerformanceReport';
import { flattenedTaskTileData } from '../../Task/KPIMetric/Helper';
import KpiTile from './MetricTile';

const KpiStyles = makeStyles<{ disableRightScroll: boolean }>()((theme, props) => ({
  tiles: {
    display: 'flex',

    overflowX: 'auto',
  },
  scrollLeft: {
    marginLeft: '10px',
    marginTop: '2.25%',
  },
  scrollRight: {
    marginRight: '10px',
    marginTop: '2.25%',
  },
  navigationIcon: {
    borderRadius: '50%',
    borderWidth: '0.5px',
    background: theme.palette.components.icons.secondary.backgroundColor,
    color: '#fff',
    height: '10px',
    width: '10px',
    '&:hover': {
      background: '#bbdefb',
    },
  },
  scroll: {
    overflow: 'hidden',
    '&:hover': {
      overflowX: props.disableRightScroll ? 'hidden' : 'auto',
    },
  },
}));
interface IKpiGridProps {
  title: TabTitle;
  kpiMetrics: any;
  onClickHandler: (label) => void;
  selectedMetric: string;
}

interface IScrollDisabler {
  disableLeftScroll: boolean;
  disableRightScroll: boolean;
}
export default function KpiMetricGrid(props: IKpiGridProps) {
  const { kpiMetrics, title } = props;
  const [disableNavigation, setDisableNavigation] = useState<IScrollDisabler>({
    disableLeftScroll: true,
    disableRightScroll: false,
  });
  const { classes, cx } = KpiStyles({ disableRightScroll: disableNavigation.disableRightScroll });
  const ref = useRef<any>(null);
  const [windowSize, setWindowSize] = useState(getWindowSize());

  const chooseMetricValues = customSwitch({
    [TabTitle.communication]: flattenedNotificationTileData(kpiMetrics),
    [TabTitle.task]: flattenedTaskTileData(kpiMetrics),
  })([]);
  const NUMBER_OF_TILES = chooseMetricValues(title).length;

  useEffect(() => {
    function handleWindowResize() {
      setWindowSize(getWindowSize());
    }
    window.addEventListener('resize', handleWindowResize);
    setDisableNavigation(prevState => ({
      ...prevState,
      disableRightScroll: NUMBER_OF_TILES * 275 < windowSize.innerWidth,
    }));

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function getWindowSize() {
    const { innerWidth, innerHeight } = window;
    return { innerWidth, innerHeight };
  }

  useEffect(() => {
    setDisableNavigation(prevState => ({
      ...prevState,
      disableRightScroll: NUMBER_OF_TILES * 275 < windowSize.innerWidth,
    }));
  }, [NUMBER_OF_TILES, windowSize]);

  const handleScroll = () => {
    setDisableNavigation({
      disableLeftScroll: ref.current.scrollLeft === 0,
      disableRightScroll: ref.current.scrollLeft === ref.current.scrollWidth - ref.current.clientWidth,
    });
  };

  const scroll = scrollOffset => {
    if (ref && ref.current) ref.current.scrollLeft += scrollOffset;
  };

  return (
    <LayoutGrid container direction="row" wrap="nowrap">
      <IconButton
        color="primary"
        disabled={disableNavigation.disableLeftScroll}
        onClick={() => scroll(-50)}
        className={cx(classes.scrollLeft, classes.navigationIcon)}
        size="small">
        <NavigateBeforeIcon />
      </IconButton>
      <div id="tile-grid" ref={ref} className={cx(classes.tiles, classes.scroll)} onScroll={handleScroll}>
        {chooseMetricValues(title)?.map((tiles, index) => {
          return (
            <LayoutGridItem key={tiles.primaryLabel}>
              <KpiTile {...tiles} {...props}></KpiTile>
            </LayoutGridItem>
          );
        })}
      </div>
      <IconButton
        color="primary"
        disabled={disableNavigation.disableRightScroll}
        onClick={() => scroll(50)}
        className={cx(classes.scrollRight, classes.navigationIcon)}
        size="small">
        <NavigateNextIcon />
      </IconButton>
    </LayoutGrid>
  );
}
